import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

function formatError(detail) {
  if (!detail) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { username: form.username, password: form.password },
        { withCredentials: true }
      );
      if (window.gtag) {
        window.gtag('event', 'login', { method: 'web' });
      }
      navigate('/');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper scrollable={false}>
      <SEOMeta title="Iniciar sesión" noIndex />
      <Navbar />
      <main className="register-main anim-fade-up" style={{ animationDelay: '0.2s' }}>
        <div className="register-card" data-testid="login-card">
          <div className="register-header">
            <p className="register-eyebrow">{t('login.eyebrow')}</p>
            <h1 className="register-title" data-testid="login-heading">{t('login.title')}</h1>
            <p className="register-subtitle">{t('login.subtitle')}</p>
          </div>

          {error && (
            <div className="register-error" data-testid="login-error">
              <AlertCircle size={15} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="register-form" data-testid="login-form">
            <div className="form-field">
              <label className="form-label">{t('login.username_label')}</label>
              <div className="input-wrap">
                <User size={15} className="input-icon" />
                <input
                  type="text"
                  name="username"
                  value={form.username}
                  onChange={handleChange}
                  placeholder={t('login.username_placeholder')}
                  className="form-input"
                  data-testid="input-username"
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="form-field">
              <label className="form-label">{t('login.password_label')}</label>
              <div className="input-wrap">
                <Lock size={15} className="input-icon" />
                <input
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder={t('login.password_placeholder')}
                  className="form-input"
                  data-testid="input-password"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="pass-toggle"
                  onClick={() => setShowPass(!showPass)}
                  data-testid="toggle-password"
                  tabIndex={-1}
                >
                  {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className={`register-btn ${loading ? 'register-btn--loading' : ''}`}
              disabled={loading}
              data-testid="login-submit"
              style={{ marginTop: '8px' }}
            >
              {loading ? t('login.submitting') : t('login.submit')}
            </button>
          </form>

          <p className="register-login-link" style={{ marginTop: '16px' }}>
            {t('login.no_account')}{' '}
            <Link to="/registro" className="terms-link" data-testid="go-to-register">
              {t('login.register_free')}
            </Link>
          </p>
          <p className="register-login-link" style={{ marginTop: '8px' }}>
            <Link to="/recuperar-contrasena" className="terms-link" data-testid="forgot-password">
              ¿Olvidaste tu contraseña?
            </Link>
          </p>
        </div>
      </main>
      <Footer />
    </PageWrapper>
  );
}
